<template>
  <teleport to="body">
    <transition
      leave-active-class="duration-200">
      <div
        v-show="show"
        class="fixed flex justify-center items-center inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50">
        <transition
          enter-active-class="ease-out duration-300"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0">
          <div
            v-show="show"
            class="fixed inset-0 transform transition-all"
            @click="close">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
        </transition>

        <transition
          enter-active-class="ease-out duration-300"
          enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to-class="opacity-100 translate-y-0 sm:scale-100"
          leave-active-class="ease-in duration-200"
          leave-from-class="opacity-100 translate-y-0 sm:scale-100"
          leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
            v-show="show"
            class="relative my-6 overflow-hidden rounded-lg bg-white shadow-xl transform transition-all sm:w-full sm:mx-auto"
            :class="maxWidthClass"
            :style="`max-height: ${resolvedMaxHeight}`">
            <div
              ref="scrollContainer"
              class="overflow-y-auto"
              :style="`max-height: ${resolvedMaxHeight}`"
              @scroll="updateScrollHint">
              <slot></slot>
            </div>

            <transition
              enter-active-class="transition duration-150"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition duration-150"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0">
              <div
                v-if="shouldShowScrollHint"
                class="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex h-16 items-end justify-center bg-gradient-to-t from-white via-white to-transparent pb-3">
                <button
                  type="button"
                  class="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm"
                  aria-label="モーダルの一番下までスクロール"
                  @click.stop="scrollToBottom">
                  <span
                    class="text-xl font-medium leading-none"
                    aria-hidden="true">↓</span>
                </button>
              </div>
            </transition>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: String,
    default: '2xl',
  },
  maxHeight: {
    type: String,
    default: '80%',
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  showScrollHint: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])
const scrollContainer = ref(null)
const shouldShowScrollHint = ref(false)

const close = () => {
  if (props.closeable) {
    emit('close')
  }
}
const closeOnEscape = (e) => {
  if (e.key === 'Escape' && props.show) {
    close()
  }
}

const maxWidthClass = computed(() => ({
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
}[props.maxWidth]))

const resolvedMaxHeight = computed(() => {
  const value = props.maxHeight.trim()

  if (value.endsWith('%')) {
    return `${value.slice(0, -1)}vh`
  }

  return value
})

const updateScrollHint = () => {
  const container = scrollContainer.value
  if (!props.showScrollHint || !container) {
    shouldShowScrollHint.value = false
    return
  }

  const scrollRemaining = container.scrollHeight - container.clientHeight - container.scrollTop
  shouldShowScrollHint.value = container.scrollHeight > container.clientHeight + 1 && scrollRemaining > 8
}

const scrollToBottom = () => {
  const container = scrollContainer.value
  if (!container) {
    return
  }

  container.scrollTo({
    top: container.scrollHeight,
    behavior: 'smooth',
  })
}

watch(
  () => props.show,
  (val) => {
    if (val) {
      document.body.style.overflow = 'hidden'
      nextTick(() => {
        updateScrollHint()
        window.setTimeout(updateScrollHint, 100)
        window.setTimeout(updateScrollHint, 300)
      })
    } else {
      document.body.style.overflow = null
      shouldShowScrollHint.value = false
    }
  },
  { immediate: true },
)

watch(
  () => props.showScrollHint,
  () => nextTick(updateScrollHint),
)

onMounted(() => {
  document.addEventListener('keydown', closeOnEscape)
  window.addEventListener('resize', updateScrollHint)
})
onUnmounted(() => {
  document.removeEventListener('keydown', closeOnEscape)
  window.removeEventListener('resize', updateScrollHint)
  document.body.style.overflow = null
})
</script>
